// Временная шкала — чистые функции (ARCHITECTURE 6.3). Алгоритм совпадает с tactics-core::timeline::position_at.
import type { Segment, StepTimeline } from "@brovka/content-types";

type Positions = StepTimeline["start"];

export type Point = [number, number];

/** Точка на ломаной на доле k ∈ [0, 1] её длины. */
export function along(path: Point[], k: number): Point {
  const first = path[0] ?? [0, 0];
  const lens: number[] = [];
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1]!;
    const b = path[i]!;
    lens.push(Math.hypot(b[0] - a[0], b[1] - a[1]));
  }
  const total = lens.reduce((s, l) => s + l, 0);
  if (total <= 0) return first;
  let left = Math.min(1, Math.max(0, k)) * total;
  for (let i = 0; i < lens.length; i++) {
    const l = lens[i]!;
    if (left <= l || i === lens.length - 1) {
      const r = l > 0 ? Math.min(1, left / l) : 1;
      const a = path[i]!;
      const b = path[i + 1]!;
      return [a[0] + (b[0] - a[0]) * r, a[1] + (b[1] - a[1]) * r];
    }
    left -= l;
  }
  return first;
}

/** Позиция объекта в момент t по сегментам шага. */
export function positionAt(segments: Segment[], obj: string, start: Point, t: number): Point {
  let pos = start;
  for (const seg of segments) {
    if (seg.obj !== obj) continue;
    if (t < seg.t0) break;
    if (t >= seg.t1) {
      pos = (seg.path[seg.path.length - 1] as Point | undefined) ?? pos;
      continue;
    }
    const k = seg.t1 > seg.t0 ? (t - seg.t0) / (seg.t1 - seg.t0) : 1;
    return along(seg.path as Point[], k);
  }
  return pos;
}

export type Pose = {
  /** Позиции всех объектов, кроме мяча. */
  bodies: Record<string, Point>;
  /** Мяч. */
  ball: Point;
  /** Мяч у игрока (рисуется у ног), а не в полёте. */
  ballAttached: boolean;
};

/** Поза кадра в момент t шага: игроки — по своим сегментам, мяч — по сегментам мяча или у владельца. */
export function poseAt(step: StepTimeline, t: number, ballId = "ball"): Pose {
  const bodies: Record<string, Point> = {};
  for (const [id, p] of Object.entries(step.start)) {
    if (id === ballId || !p) continue;
    bodies[id] = positionAt(step.segments, id, p as Point, t);
  }
  const ballSegs = step.segments.filter((s) => s.obj === ballId);
  const startBall = (step.start[ballId] as Point | undefined) ?? [50, 50];
  let current: Segment | undefined;
  for (const s of ballSegs) {
    if (s.t0 <= t) current = s;
  }
  if (!current) {
    const owner = step.ownerStart;
    return owner && bodies[owner] ? { bodies, ball: bodies[owner]!, ballAttached: true } : { bodies, ball: startBall, ballAttached: false };
  }
  if (t < current.t1) {
    const k = current.t1 > current.t0 ? (t - current.t0) / (current.t1 - current.t0) : 1;
    // При ведении мяч у ног игрока.
    return { bodies, ball: along(current.path as Point[], k), ballAttached: current.kind === "carry" };
  }
  if (current.holder && bodies[current.holder]) return { bodies, ball: bodies[current.holder]!, ballAttached: true };
  return { bodies, ball: (current.path[current.path.length - 1] as Point | undefined) ?? startBall, ballAttached: false };
}

/** Поза конца шага. */
export function endPose(step: StepTimeline, ballId = "ball"): Pose {
  return poseAt(step, step.duration + 1, ballId);
}

/** Поза статичного кадра (задание, превью). */
export function staticPose(positions: Positions, owner: string | null | undefined, ballId = "ball"): Pose {
  const bodies: Record<string, Point> = {};
  for (const [id, p] of Object.entries(positions)) if (id !== ballId && p) bodies[id] = p as Point;
  const ball = owner && bodies[owner] ? bodies[owner]! : ((positions[ballId] as Point | undefined) ?? [50, 50]);
  return { bodies, ball, ballAttached: Boolean(owner && bodies[owner]) };
}

/** Простой шаг для анимаций-последствий в задании: последовательные перемещения. */
export type SimpleMove =
  | { kind: "run" | "dribble"; obj: string; to: Point }
  | { kind: "pass"; to: string | Point }
  | { kind: "tackle"; by: string };

const SPEED = { run: 4, dribble: 3, pass: 10 } as const;

/** Построить шаг из простых перемещений (скорости — как в движке, в метрах эталонного поля). */
export function buildStep(start: Record<string, Point>, owner: string | null, moves: SimpleMove[], field: { lengthM: number; widthM: number }, ballId = "ball"): StepTimeline {
  const cur: Record<string, Point> = { ...start };
  const m = (a: Point, b: Point) => Math.hypot(((b[0] - a[0]) / 100) * field.lengthM, ((b[1] - a[1]) / 100) * field.widthM);
  let t = 0;
  let holder = owner;
  const segments: Segment[] = [];
  for (const mv of moves) {
    if (mv.kind === "run" || mv.kind === "dribble") {
      const from = cur[mv.obj] ?? mv.to;
      const d = Math.max(0.25, m(from, mv.to) / SPEED[mv.kind]);
      segments.push({ obj: mv.obj, kind: mv.kind, t0: t, t1: t + d, path: [from, mv.to] });
      if (mv.kind === "dribble") segments.push({ obj: ballId, kind: "carry", t0: t, t1: t + d, path: [from, mv.to], holder: mv.obj });
      cur[mv.obj] = mv.to;
      t += d;
    } else if (mv.kind === "pass") {
      const from = (holder && cur[holder]) || cur[ballId] || [50, 50];
      const target: Point = typeof mv.to === "string" ? (cur[mv.to] ?? from) : mv.to;
      const d = Math.max(0.25, m(from, target) / SPEED.pass);
      segments.push({ obj: ballId, kind: "pass", t0: t, t1: t + d, path: [from, target], holder: typeof mv.to === "string" ? mv.to : undefined });
      holder = typeof mv.to === "string" ? mv.to : null;
      cur[ballId] = target;
      t += d;
    } else if (mv.kind === "tackle") {
      const from = (holder && cur[holder]) || cur[ballId] || [50, 50];
      const to = cur[mv.by] ?? from;
      segments.push({ obj: ballId, kind: "tackle", t0: t, t1: t + 0.4, path: [from, to], holder: mv.by });
      holder = mv.by;
      t += 0.4;
    }
  }
  const startPos: Positions = { ...start };
  if (owner && start[owner]) startPos[ballId] = start[owner];
  return { label: "", duration: t + 0.4, start: startPos, end: { ...cur }, ownerStart: owner ?? undefined, ownerEnd: holder ?? undefined, segments, overlays: [] };
}
